import React, { useState } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { Lock, Plus, Trash2, UserCheck, Shield } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const RESTRICTED_STATUSES = ["approved", "active", "expired", "terminated"];

export function AccessControlPanel({ contract, users, onRefresh }) {
  const { user } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState("");
  const [adding, setAdding] = useState(false);

  const isRestricted = RESTRICTED_STATUSES.includes(contract.status);
  const isCreator = contract.created_by === user?.id;
  const isOwner = contract.contract_owner_id === user?.id;
  const isAdmin = user?.role === "superuser" || user?.role === "admin";
  const canManage = isCreator || isOwner || isAdmin;

  const contractUsers = contract.contract_users || [];
  const creatorName = (users || []).find((u) => u.id === contract.created_by)?.name || "Creator";
  const ownerName = contract.contract_owner_id ? ((users || []).find((u) => u.id === contract.contract_owner_id)?.name || "Owner") : "";

  // Filter out users who already have access or are the creator/owner
  const availableUsers = (users || []).filter(
    (u) => u.id !== contract.created_by && u.id !== contract.contract_owner_id && !contractUsers.some((cu) => cu.user_id === u.id)
  );

  const handleAdd = async () => {
    if (!selectedUserId) return;
    setAdding(true);
    try {
      await apiClient.post(`/contracts/${contract.id}/users`, { user_id: selectedUserId });
      toast.success("Access granted");
      setSelectedUserId("");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add user");
    } finally { setAdding(false); }
  };

  const handleRemove = async (userId) => {
    try {
      await apiClient.delete(`/contracts/${contract.id}/users/${userId}`);
      toast.success("Access revoked");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove user");
    }
  };

  return (
    <Card data-testid="access-control-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Lock size={14} /> Access Control
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!isRestricted && (
          <p className="text-xs text-slate-400 italic">Access restrictions apply after approval. Currently open to all permitted users.</p>
        )}
        {isRestricted && (
          <div className="p-2 border border-amber-200 bg-amber-50/50 rounded-sm">
            <p className="text-[10px] font-mono uppercase text-amber-600">Restricted Access</p>
            <p className="text-xs text-slate-500 mt-0.5">Only listed users can view this contract's details.</p>
          </div>
        )}

        {/* Creator & Owner */}
        <div className="space-y-1.5">
          <p className="text-[10px] font-mono uppercase text-slate-400">Full Access</p>
          <AccessRow name={creatorName} role="Creator" level="full" />
          {contract.contract_owner_id && contract.contract_owner_id !== contract.created_by && (
            <AccessRow name={ownerName} role="Owner" level="full" />
          )}
        </div>

        {/* Contract Users */}
        {contractUsers.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-mono uppercase text-slate-400">Read-Only Users</p>
            {contractUsers.map((cu) => (
              <AccessRow
                key={cu.user_id}
                name={cu.user_name}
                role={cu.user_email || "Contract User"}
                level="read_only"
                onRemove={canManage ? () => handleRemove(cu.user_id) : undefined}
              />
            ))}
          </div>
        )}

        {/* Add User */}
        {canManage && (
          <div className="pt-2 border-t space-y-2">
            <p className="text-[10px] font-mono uppercase text-slate-400">Grant Read-Only Access</p>
            <div className="flex gap-1.5">
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="rounded-sm text-xs h-8 flex-1" data-testid="add-user-select">
                  <SelectValue placeholder="Select user..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id} className="text-xs">
                      {u.name} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={handleAdd}
                disabled={!selectedUserId || adding}
                className="rounded-sm h-8 w-8 p-0"
                data-testid="add-user-btn"
              >
                <Plus size={14} />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AccessRow({ name, role, level, onRemove }) {
  return (
    <div className="flex items-center justify-between p-1.5 rounded-sm hover:bg-slate-50 dark:hover:bg-slate-800/50">
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0">
          {level === "full" ? <Shield size={12} className="text-indigo-500" /> : <UserCheck size={12} className="text-slate-400" />}
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium truncate">{name}</p>
          <p className="text-[10px] text-slate-400 truncate">{role}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <Badge className={`rounded-sm text-[9px] ${level === "full" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>
          {level === "full" ? "Full" : "Read-Only"}
        </Badge>
        {onRemove && (
          <Button size="sm" variant="ghost" onClick={onRemove} className="h-6 w-6 p-0 text-slate-400 hover:text-red-500" data-testid={`remove-user-${name}`}>
            <Trash2 size={12} />
          </Button>
        )}
      </div>
    </div>
  );
}
